import dayjs from "dayjs";
import { ISubscriptionOverview } from "@api-clients/subscriptions";
import { CatsAPI, SubscriptionFilter } from "@api-clients/cats";

import { Allergen, SubscriptionItemType } from "@/primary-models";
import { FREE_SPRINKLES_LAUNCH_DATE } from "@/constants/products";

const SPRINKLES_ALLERGENS: Set<Allergen> = new Set([
  Allergen.CHICKEN,
]);

function qualifiesForFreeSprinklesOffer(
  chargeCount: number,
  lastCharge: string | null | undefined,
): boolean {
  if (chargeCount === 1) {
    return true;
  }

  // IF they have been charged after the launch date
  // we know they have gotten/will get the free sprinkles
  // we don't show this offer anymore
  if (!dayjs(lastCharge).isBefore(FREE_SPRINKLES_LAUNCH_DATE)) {
    return false;
  }

  return chargeCount > 1;
}

export async function checkFreeSprinklesEligibility(
  catsAPI: CatsAPI,
  subscriptionId: number,
  subscriptionsOverview: ISubscriptionOverview,
): Promise<boolean> {
  if (!catsAPI) {
    return false;
  }

  const sub = subscriptionsOverview.freshMealPlans.find((plan) => plan.id === subscriptionId);
  if (!sub?.fulfillment) {
    return false;
  }

  const { fulfillment } = sub;
  const lastCharge = sub.date.lastCharge;

  // IF they are not already subscribed to sprinkles
  const sprinklesItem = sub.items.find((item) => item.name === SubscriptionItemType.SPRINKLES);
  const hasPaidSprinkles =
    (sprinklesItem?.quantity ?? 0) > 0 && sprinklesItem?.postChargeQuantity === null;
  if (hasPaidSprinkles) {
    return false;
  }

  // AND IF the cat is not allergic to chicken
  const { cats } = await catsAPI.getCats({
    filterSubscription: SubscriptionFilter.HAS_FRESH,
    showSuppressedCats: false,
  });
  const thisCat = cats.find((c) => c.subscription?.subscriptionId === subscriptionId);
  if (thisCat?.details.allergies?.some((allergy) => SPRINKLES_ALLERGENS.has(allergy))) {
    return false;
  }

  // First-box trialists receive free sprinkles; existing customers whose last charge
  // was before launch get them on their next box.
  return qualifiesForFreeSprinklesOffer(fulfillment.chargeCount, lastCharge);
}
